import type { StudioPageRole } from "../types/project";
import type {
  WebsiteGraphAnalysis,
  WebsiteGraphNavigationLink,
} from './website-graph-analyzer';
import type {
  WebsiteGraphAsset,
  WebsiteGraphPage,
  WebsiteRelationshipGraph,
} from './website-relationship-graph';

export type WebsitePageRole = StudioPageRole;

export type WebsitePageClassification = {
  pageId: string;
  route: string;
  title: string;
  role: WebsitePageRole;
  confidence: number;
  reasons: string[];
  isPrimary: boolean;
  isNavigationCandidate: boolean;
};

export type WebsiteSemanticClassification = {
  homepage: WebsitePageClassification | null;
  pages: WebsitePageClassification[];
  primaryPages: WebsitePageClassification[];
  secondaryPages: WebsitePageClassification[];
  navigationPages: WebsitePageClassification[];
  logoAsset: WebsiteGraphAsset | null;
  globalAssets: WebsiteGraphAsset[];
};

type RoleEvidence = {
  role: WebsitePageRole;
  score: number;
  reasons: string[];
};

const ROLE_PATTERNS: Array<{
  role: WebsitePageRole;
  patterns: RegExp[];
}> = [
  {
    role: 'about',
    patterns: [
      /\babout\b/i,
      /\bour story\b/i,
      /\bwho we are\b/i,
      /\bcompany\b/i,
    ],
  },
  {
    role: 'services',
    patterns: [
      /\bservices?\b/i,
      /\bwhat we do\b/i,
      /\bsolutions?\b/i,
      /\bcapabilities\b/i,
    ],
  },
  {
    role: 'contact',
    patterns: [
      /\bcontact\b/i,
      /\bget in touch\b/i,
      /\breach us\b/i,
      /\brequest (?:a )?(?:quote|estimate)\b/i,
    ],
  },
  {
    role: 'gallery',
    patterns: [
      /\bgallery\b/i,
      /\bphotos?\b/i,
      /\bimages?\b/i,
    ],
  },
  {
    role: 'portfolio',
    patterns: [
      /\bportfolio\b/i,
      /\bprojects?\b/i,
      /\bour work\b/i,
      /\bcase studies\b/i,
    ],
  },
  {
    role: 'testimonials',
    patterns: [
      /\btestimonials?\b/i,
      /\breviews?\b/i,
      /\bwhat (?:our )?customers say\b/i,
    ],
  },
  {
    role: 'blog',
    patterns: [
      /\bblog\b/i,
      /\bnews\b/i,
      /\barticles?\b/i,
      /\binsights?\b/i,
      /\bresources?\b/i,
    ],
  },
  {
    role: 'faq',
    patterns: [
      /\bfaq\b/i,
      /\bfrequently asked questions\b/i,
      /\bquestions\b/i,
    ],
  },
  {
    role: 'pricing',
    patterns: [
      /\bpricing\b/i,
      /\bplans?\b/i,
      /\bpackages?\b/i,
      /\brates?\b/i,
    ],
  },
  {
    role: 'team',
    patterns: [
      /\bteam\b/i,
      /\bstaff\b/i,
      /\bleadership\b/i,
      /\bmeet the\b/i,
    ],
  },
  {
    role: 'legal',
    patterns: [
      /\bprivacy\b/i,
      /\bterms\b/i,
      /\bcookies?\b/i,
      /\bdisclaimer\b/i,
      /\blegal\b/i,
    ],
  },
  {
    role: 'utility',
    patterns: [
      /\blogin\b/i,
      /\bsign in\b/i,
      /\baccount\b/i,
      /\bsearch\b/i,
      /\bsitemap\b/i,
      /\bthank you\b/i,
      /\b404\b/i,
    ],
  },
];

function normalizeEvidenceText(page: WebsiteGraphPage): string {
  return [
    page.route.replace(/[-_/]+/g, ' '),
    page.title,
  ]
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function clampConfidence(value: number): number {
  return Math.max(0, Math.min(1, value));
}

function scoreRolePatterns(
  page: WebsiteGraphPage,
): RoleEvidence[] {
  const evidenceText = normalizeEvidenceText(page);
  const results: RoleEvidence[] = [];

  for (const definition of ROLE_PATTERNS) {
    const reasons: string[] = [];
    let score = 0;

    for (const pattern of definition.patterns) {
      if (pattern.test(page.route)) {
        score += 0.65;
        reasons.push(
          `Route matches ${definition.role} terminology`,
        );
        break;
      }
    }

    for (const pattern of definition.patterns) {
      if (pattern.test(page.title)) {
        score += 0.45;
        reasons.push(
          `Title matches ${definition.role} terminology`,
        );
        break;
      }
    }

    if (
      score === 0 &&
      definition.patterns.some((pattern) =>
        pattern.test(evidenceText),
      )
    ) {
      score += 0.25;
      reasons.push(
        `Page evidence suggests ${definition.role}`,
      );
    }

    if (score > 0) {
      results.push({
        role: definition.role,
        score,
        reasons,
      });
    }
  }

  return results;
}

function isLikelyBlogPost(page: WebsiteGraphPage): boolean {
  const routeSegments = page.route
    .split('/')
    .filter(Boolean);

  return (
    routeSegments.length >= 2 &&
    routeSegments.some((segment) =>
      /blog|news|article|insight|resource/i.test(segment),
    )
  );
}

function classifyPageRole(
  page: WebsiteGraphPage,
): RoleEvidence {
  if (page.route === '/') {
    return {
      role: 'home',
      score: 1,
      reasons: ['Page uses the root route'],
    };
  }

  const roleEvidence = scoreRolePatterns(page);

  if (isLikelyBlogPost(page)) {
    roleEvidence.push({
      role: 'blog',
      score: 0.55,
      reasons: ['Page is nested beneath a content route'],
    });
  }

  if (roleEvidence.length === 0) {
    return {
      role: 'other',
      score: 0.2,
      reasons: ['No recognized semantic page pattern'],
    };
  }

  return roleEvidence.sort((left, right) => {
    if (right.score !== left.score) {
      return right.score - left.score;
    }

    return left.role.localeCompare(right.role);
  })[0];
}

function navigationPageIds(
  navigationLinks: WebsiteGraphNavigationLink[],
): Set<string> {
  return new Set(
    navigationLinks.map((link) => link.targetPageId),
  );
}

function primaryPageIds(
  analysis: WebsiteGraphAnalysis,
): Set<string> {
  return new Set(
    analysis.primaryPages.map((page) => page.id),
  );
}

function rolePriority(role: WebsitePageRole): number {
  const priorities: Record<WebsitePageRole, number> = {
    home: 0,
    about: 1,
    services: 2,
    service: 2,
    portfolio: 3,
    gallery: 4,
    testimonials: 5,
    pricing: 6,
    faq: 7,
    contact: 8,
    team: 9,
    blog: 10,
    legal: 11,
    utility: 12,
    other: 13,
  };

  return priorities[role];
}

function sortClassifications(
  pages: WebsitePageClassification[],
): WebsitePageClassification[] {
  return [...pages].sort((left, right) => {
    const priorityDifference =
      rolePriority(left.role) -
      rolePriority(right.role);

    if (priorityDifference !== 0) {
      return priorityDifference;
    }

    if (right.confidence !== left.confidence) {
      return right.confidence - left.confidence;
    }

    return left.route.localeCompare(right.route);
  });
}

function selectLogoAsset(
  analysis: WebsiteGraphAnalysis,
): WebsiteGraphAsset | null {
  return analysis.likelyLogoAssets[0] ?? null;
}

export function classifyWebsiteSemantics(
  graph: WebsiteRelationshipGraph,
  analysis: WebsiteGraphAnalysis,
): WebsiteSemanticClassification {
  const primaryIds = primaryPageIds(analysis);
  const navigationIds = navigationPageIds(
    analysis.likelyNavigationLinks,
  );

  const pages = graph.pages.map((page) => {
    const evidence = classifyPageRole(page);
    const reasons = [...evidence.reasons];

    let confidence = evidence.score;

    if (navigationIds.has(page.id)) {
      confidence += 0.1;
      reasons.push(
        'Page appears repeatedly in site navigation',
      );
    }

    if (primaryIds.has(page.id)) {
      confidence += 0.05;
      reasons.push(
        'Page is part of the primary graph structure',
      );
    }

    if (page.isOrphan) {
      confidence -= 0.15;
      reasons.push('Page is orphaned in the site graph');
    }

    return {
      pageId: page.id,
      route: page.route,
      title: page.title,
      role: evidence.role,
      confidence: clampConfidence(confidence),
      reasons,
      isPrimary:
        primaryIds.has(page.id) &&
        evidence.role !== 'legal' &&
        evidence.role !== 'utility',
      isNavigationCandidate:
        navigationIds.has(page.id),
    };
  });

  const sortedPages = sortClassifications(pages);

  const primaryPages = sortedPages.filter(
    (page) => page.isPrimary,
  );

  const secondaryPages = sortedPages.filter(
    (page) => !page.isPrimary,
  );

  const navigationPages = sortedPages.filter(
    (page) => page.isNavigationCandidate,
  );

  return {
    homepage:
      sortedPages.find((page) => page.role === 'home') ??
      null,
    pages: sortedPages,
    primaryPages,
    secondaryPages,
    navigationPages,
    logoAsset: selectLogoAsset(analysis),
    globalAssets: [...analysis.globalAssets],
  };
}
